import React, { useState, useEffect } from "react";
import axios from "../../utils/axiosConfig";
import "./StandardMaster.css";

const STANDARD_TYPES = [
  { id: "size", title: "Size", subtitle: "Valve size standards" },
  { id: "class", title: "Class", subtitle: "Pressure class standards" },
  { id: "design_standard", title: "DESIGN STANDARD", subtitle: "Design standard & edition" },
  { id: "testing_standard", title: "TESTING STD. AND EDITION", subtitle: "Testing standard & edition" },
  { id: "end_connection", title: "END CONN. STD. & ED.", subtitle: "End connection standard & edition" },
  { id: "raised_face", title: "RAISED FACE STD. & ED.", subtitle: "Raised face standard & edition" },
  { id: "heat_number", title: "HEAT NUMBER", subtitle: "Heat numbers for Heat Annexure" },
  { id: "component", title: "COMPONENT", subtitle: "Components for Heat Annexure (BODY, ADAPTER, etc.)" },
  { id: "special_testing", title: "SPECIAL TESTING & OTHER REQUIREMENTS", subtitle: "Special testing requirements and other configurations" },
  { id: "declaration_of_compliance", title: "DECLARATION OF COMPLIANCE", subtitle: "Valve automation compliance statements" },
];

function StandardMaster() {
  const [standards, setStandards] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeModal, setActiveModal] = useState(null);
  const [values, setValues] = useState([]);
  const [newValue, setNewValue] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    fetchAllStandards();
  }, []);

  const fetchAllStandards = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/standards");
      setStandards(response.data.data || {});
    } catch (error) {
      console.error("Error fetching standards:", error);
      setStandards({});
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 3000);
  };

  const openModal = async (standardType) => {
    setActiveModal(standardType);
    setNewValue("");
    try {
      const response = await axios.get(`/standards/${standardType}`);
      setValues(response.data.data || []);
    } catch (error) {
      setValues([]);
    }
  };

  const closeModal = () => {
    setActiveModal(null);
    setValues([]);
    setNewValue("");
  };

  const handleAddValue = async (e) => {
    e.preventDefault();
    if (!newValue.trim()) return;

    try {
      await axios.post("/standards", { type: activeModal, value: newValue.trim() });
      showMessage("success", "Value added successfully");
      const response = await axios.get(`/standards/${activeModal}`);
      setValues(response.data.data || []);
      setNewValue("");
      // Refresh main standards counts
      fetchAllStandards();
    } catch (error) {
      showMessage("error", error.response?.data?.message || "Failed to add value");
    }
  };

  const handleDeleteValue = async (valueId) => {
    try {
      await axios.delete(`/standards/${activeModal}/${valueId}`);
      showMessage("success", "Value deleted successfully");
      const response = await axios.get(`/standards/${activeModal}`);
      setValues(response.data.data || []);
      // Refresh main standards counts
      fetchAllStandards();
    } catch (error) {
      showMessage("error", error.response?.data?.message || "Failed to delete value");
    }
  };

  return (
    <div className="tcds-container">
      <div className="tcds-header">
        <h1 className="tcds-title">Standard Master</h1>
        <p className="tcds-subtitle">
          Select the type of standard to manage values
        </p>
      </div>

      {message.text && (
        <div className={`alert-message alert-${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="tcds-type-selection">
        <div className="tcds-type-cards">
          {STANDARD_TYPES.map((std, index) => (
            <div
              key={std.id}
              className={`tcds-type-card ${index === 0 ? 'automation' : index === 1 ? 'api-6d' : 'api-600'}`}
              onClick={() => openModal(std.id)}
            >
              <div className="tcds-card-content">
                <h3 className="tcds-card-title">{std.title}</h3>
                <span className="tcds-card-badge">{standards[std.id]?.length || 0} values</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal for managing values */}
      {activeModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{STANDARD_TYPES.find((s) => s.id === activeModal)?.title}</h2>
              <button className="modal-close" onClick={closeModal}>
                ×
              </button>
            </div>
            <div className="modal-content">
              <form onSubmit={handleAddValue} style={{ display: "flex", gap: "12px", marginBottom: "20px", flexDirection: activeModal === "declaration_of_compliance" ? "column" : "row" }}>
                {activeModal === "declaration_of_compliance" ? (
                  <textarea
                    className="form-input"
                    placeholder="Enter compliance statement..."
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    style={{ flex: 1, padding: "10px 14px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", minHeight: "100px", fontFamily: "inherit" }}
                    autoFocus
                  />
                ) : (
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter new value..."
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    style={{ flex: 1, padding: "10px 14px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px" }}
                    autoFocus
                  />
                )}
                <button type="submit" className="btn-create-tcds" style={{ alignSelf: activeModal === "declaration_of_compliance" ? "flex-end" : "auto" }}>
                  Add
                </button>
              </form>

              <div className="values-list">
                {values.length === 0 ? (
                  <div className="empty-state">
                    <p>No values added yet</p>
                  </div>
                ) : (
                  <div className="tcds-table-container">
                    <table className="tcds-table">
                      <thead>
                        <tr>
                          <th>Value</th>
                          <th style={{ width: "100px" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {values.map((val) => (
                          <tr key={val.id}>
                            <td>{val.value}</td>
                            <td>
                              <div className="action-buttons">
                                <button
                                  className="btn-action btn-delete"
                                  onClick={() => handleDeleteValue(val.id)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
                <button
                  className="btn-cancel"
                  onClick={closeModal}
                  style={{
                    padding: "10px 20px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: 600,
                    background: "#fff",
                    color: "#374151",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StandardMaster;
